import { useContext } from "react";
import { CommonContext } from "config/commonProvider";
import { ELEMENTS_IDS } from "constants/ElementsIds";
import { getFieldName } from "helpers/myLivestock";
import { handleErrorCheck, removeNothings } from "helpers/general";
import FormBuilder from "components/Common/FormBuilder";
import { FieldSelect, Modal } from "components";
import { useFieldNamesQuery, useMoveAnimalsToFieldMutation } from "generated/graphql";
import { getBUFromLocalStorage } from "helpers/storage";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { TABLE_IDS } from "constants/Interface";
import { capitaliseFirstLetter } from "helpers/translations/src";

type AnimalId = string;

interface Props {
  showModal: boolean;
  animalIds: Array<AnimalId>;
  onClose: () => void;
}

export const MoveToFieldModal = ({ showModal, animalIds, onClose }: Props) => {
  const { showNotification } = useContext(CommonContext);
  const businessUnitId = getBUFromLocalStorage();
  const { setTableOptions } = useContext(TableOptionsContext);

  const { data: fieldsData, loading: loadingFieldNames } = useFieldNamesQuery({
    variables: {
      businessUnitId,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });
  const [moveAnimalsToField, { loading: loadingMutation }] = useMoveAnimalsToFieldMutation();

  const onSubmit = async ({ field }: { field: string }) => {
    try {
      const result = await moveAnimalsToField({
        variables: {
          input: {
            animals: animalIds,
            businessUnit: businessUnitId,
            field,
          },
        },
        refetchQueries: ["GetAnimalsPage"],
      });

      const errors = result?.data?.moveAnimalsToField?.errors || result?.errors;

      if (errors && errors.length > 0) {
        const errorMessage = capitaliseFirstLetter(errors[0]?.message || null) || "Error moving animal(s) to location.";

        showNotification({
          variant: "error",
          message: errorMessage,
        });
      } else {
        showNotification({
          message: "Animal(s) successfully moved to location",
        });

        setTableOptions({
          id: TABLE_IDS.ANIMAL_LIST_SERVER,
          selectedRows: [],
          selectedRowsIds: new Set(),
        });

        onClose();
      }
    } catch (error: unknown) {
      showNotification({
        variant: "error",
        message: "Error moving animals",
      });
    }
  };

  const fields = removeNothings(fieldsData?.fields || []);

  const fieldsList = fields.map((item) => {
    const label = getFieldName({ fieldName: item.name, unitName: item.location?.unitName });

    return {
      key: item.id,
      value: item.id,
      label,
    };
  });

  return (
    <FormBuilder
      enableReinitialize
      validateOnChange={false}
      initialValues={{
        field: "",
      }}
      onSubmit={onSubmit}
    >
      {({ values, errors, handleChange, handleSubmit }): JSX.Element => (
        <Modal
          actions={{
            primary: {
              id: ELEMENTS_IDS.MOVE_TO_FIELD_MODAL_CONFIRM_BUTTON,
              caption: "Confirm",
              disabled: loadingMutation || loadingFieldNames || values.field === "",
              requesting: loadingMutation || loadingFieldNames,
              onClick: () => handleSubmit(values.field),
            },
            secondary: {
              id: ELEMENTS_IDS.MOVE_TO_FIELD_MODAL_CANCEL_BUTTON,
              caption: "Cancel",
              colour: "grey",
              variant: "hollow",
              onClick: onClose,
            },
          }}
          active={showModal}
          handleClose={onClose}
          subTitle={`Please select a location to move these ${animalIds.length} animal(s) to`}
          title="Move to location"
        >
          <FieldSelect
            error={handleErrorCheck(errors.field)}
            inputProps={{
              name: "field",
              value: values.field,
              placeholder: "No location",
              options: fieldsList,
              onChange: handleChange,
              isClearable: true,
            }}
          />
        </Modal>
      )}
    </FormBuilder>
  );
};
